import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Policies.css';

const CookiePolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="policy-container">
      <button className="back-button" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <h1>Cookie Policy</h1>
      <p className="policy-intro">
        Last Updated: January 2025
      </p>

      <section className="policy-section">
        <h2>What Are Cookies?</h2>
        <p>
          Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website.
          They are widely used to make websites work more efficiently and provide information to website owners.
        </p>
        <p>
          At Fervoir, we use cookies and similar tracking technologies to improve your experience, analyze how our service
          is used, and deliver personalized content and advertising.
        </p>
      </section>

      <section className="policy-section">
        <h2>Types of Cookies We Use</h2>

        <div className="policy-point">
          <h3>1. Essential Cookies</h3>
          <p>
            These cookies are necessary for the website to function properly. They enable basic functions like page navigation,
            access to secure areas, and authentication. The website cannot function properly without these cookies.
          </p>
          <ul>
            <li>Session management and authentication</li>
            <li>Security and fraud prevention</li>
            <li>Load balancing and site performance</li>
          </ul>
        </div>

        <div className="policy-point">
          <h3>2. Performance and Analytics Cookies</h3>
          <p>
            These cookies help us understand how visitors interact with our website by collecting and reporting information
            anonymously. This helps us improve how our website works.
          </p>
          <ul>
            <li>Google Analytics for tracking page views and user behavior</li>
            <li>Performance monitoring and error tracking</li>
            <li>Understanding which features are most popular</li>
          </ul>
        </div>

        <div className="policy-point">
          <h3>3. Functional Cookies</h3>
          <p>
            These cookies enable enhanced functionality and personalization. They may be set by us or by third-party providers
            whose services we have added to our pages.
          </p>
          <ul>
            <li>Remember your preferences and settings</li>
            <li>Store your login information</li>
            <li>Customize your experience based on your profile</li>
            <li>Language and region preferences</li>
          </ul>
        </div>

        <div className="policy-point">
          <h3>4. Targeting and Advertising Cookies</h3>
          <p>
            These cookies are used to deliver advertisements that are relevant to you and your interests. They also help
            us measure the effectiveness of advertising campaigns.
          </p>
          <ul>
            <li>Track your browsing activity to show relevant ads</li>
            <li>Limit the number of times you see an advertisement</li>
            <li>Measure advertising campaign effectiveness</li>
            <li>Understand your interests and preferences</li>
          </ul>
        </div>
      </section>

      <section className="policy-section">
        <h2>Third-Party Cookies</h2>
        <p>
          We may use third-party service providers who may set cookies on your device on our behalf. These include:
        </p>
        <ul>
          <li><strong>Google Analytics:</strong> For website analytics and performance tracking</li>
          <li><strong>Social Media Platforms:</strong> To enable social sharing features</li>
          <li><strong>Payment Processors:</strong> To process subscription payments securely</li>
          <li><strong>Advertising Networks:</strong> To deliver targeted advertisements</li>
        </ul>
        <p>
          These third parties have their own privacy policies which govern their use of information.
        </p>
      </section>

      <section className="policy-section">
        <h2>How Long Do Cookies Last?</h2>

        <div className="policy-point">
          <h3>Session Cookies</h3>
          <p>
            These are temporary cookies that expire when you close your browser. They are used to maintain your session
            while you navigate through our website.
          </p>
        </div>

        <div className="policy-point">
          <h3>Persistent Cookies</h3>
          <p>
            These cookies remain on your device for a set period or until you delete them. They help us recognize you when
            you return to our website and remember your preferences.
          </p>
        </div>
      </section>

      <section className="policy-section">
        <h2>Managing Your Cookie Preferences</h2>

        <div className="policy-point">
          <h3>Browser Settings</h3>
          <p>
            Most web browsers allow you to control cookies through their settings. You can set your browser to:
          </p>
          <ul>
            <li>Block all cookies</li>
            <li>Only accept first-party cookies</li>
            <li>Delete cookies when you close your browser</li>
            <li>Alert you before accepting cookies</li>
          </ul>
          <p>
            Please note that if you disable or delete cookies, some features of our website may not function properly,
            and your user experience may be limited.
          </p>
        </div>

        <div className="policy-point">
          <h3>Cookie Preference Center</h3>
          <p>
            You can manage your cookie preferences directly through our Cookie Preference Center in your account settings.
            Here you can:
          </p>
          <ul>
            <li>Enable or disable different types of cookies</li>
            <li>View which cookies are active on your device</li>
            <li>Update your preferences at any time</li>
          </ul>
        </div>

        <div className="policy-point">
          <h3>Opt-Out of Analytics</h3>
          <p>
            You can opt-out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on,
            available at: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</a>
          </p>
        </div>
      </section>

      <section className="policy-section">
        <h2>Mobile Device Identifiers</h2>
        <p>
          When you use our mobile app, we may use mobile device identifiers and similar technologies to track usage and
          provide personalized experiences. You can control these through your device settings:
        </p>
        <ul>
          <li><strong>iOS:</strong> Settings → Privacy → Advertising → Limit Ad Tracking</li>
          <li><strong>Android:</strong> Settings → Google → Ads → Opt out of Ads Personalization</li>
        </ul>
      </section>

      <section className="policy-section">
        <h2>Do Not Track Signals</h2>
        <p>
          Some web browsers have a "Do Not Track" feature that signals to websites that you do not want your online
          activities tracked. Currently, there is no industry standard for how to respond to Do Not Track signals.
          At this time, our website does not respond to Do Not Track browser settings.
        </p>
      </section>

      <section className="policy-section">
        <h2>Changes to This Cookie Policy</h2>
        <p>
          We may update this Cookie Policy from time to time to reflect changes in our practices or for legal,
          operational, or regulatory reasons. We will notify you of any material changes by posting the updated policy
          on our website with a new "Last Updated" date.
        </p>
      </section>

      <section className="policy-section">
        <h2>Contact Us</h2>
        <p>
          If you have any questions about our use of cookies or this Cookie Policy, please contact us:
        </p>
        <ul>
          <li>Email: paulfortuneltd@gmail.com</li>
          <li>Visit our Contact page for more options</li>
        </ul>
      </section>

      <div className="policy-footer">
        <p>
          By continuing to use Fervoir, you consent to our use of cookies as described in this policy.
          You can change your cookie preferences at any time through your browser settings or our Cookie Preference Center.
        </p>
      </div>
    </div>
  );
};

export default CookiePolicy;
